import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Button,
    Modal,
    Dropdown,
    ButtonGroup,
} from "react-bootstrap";
import { getNewsShareUrl, getNewsSocialShareUrl } from "../utils/share";

// ✅ Clean BASE
const BASE = (() => {
    let url = process.env.REACT_APP_API_URL || "https://proudtapi.onrender.com";
    url = url.replace(/\/$/, "");
    if (url.endsWith("/api")) url = url.slice(0, -4);
    return url;
})();

const API_URL = `${BASE}/api/news`;

const Business = () => {
    const [businessNews, setBusinessNews] = useState([]);
    const [show, setShow] = useState(false);
    const [selectedNews, setSelectedNews] = useState(null);

    useEffect(() => {
        fetchBusinessNews();
    }, []);

    // ✅ FIXED IMAGE HANDLER
    const getImage = (item) => {
        const placeholder = "https://placehold.co/1200x650?text=No+Image";
        if (!item) return placeholder;

        const resolveUrl = (path) => {
            if (!path || typeof path !== "string" || path.trim() === "") return null;
            const p = path.trim();

            if (p.startsWith("http://") || p.startsWith("https://")) return p; // Cloudinary

            if (p.startsWith("/uploads/")) return `${BASE}${p}`;
            if (p.startsWith("uploads/")) return `${BASE}/${p}`;
            if (p.includes(".")) return `${BASE}/uploads/${p}`;

            return null;
        };

        return (
            resolveUrl(item.imagePath) ||
            resolveUrl(item.image) ||
            resolveUrl(item.imageUrl) ||
            resolveUrl(item.imageURL) ||
            resolveUrl(item.thumbnail) ||
            placeholder
        );
    };

    const handleImgError = (e) => {
        e.target.onerror = null;
        e.target.src = "https://placehold.co/1200x650?text=Image+Not+Found";
    };

    // ✅ VIEWS
    const getViews = (item) => {
        const v =
            item?.views ??
            item?.viewCount ??
            item?.totalViews ??
            item?.impressions ??
            0;
        return Number(v) || 0;
    };

    const formatViews = (n) => {
        if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
        if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
        return n;
    };

    // ✅ FETCH
    const fetchBusinessNews = async () => {
        try {
            const res = await fetch(API_URL);
            const result = await res.json();
            const data = result?.data || [];

            const business = data.filter(item =>
                ["business", "stock", "market", "startup", "economy"].includes(
                    item.category?.toLowerCase()
                )
            );

            setBusinessNews(business);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    // ✅ MODAL + VIEW COUNT
    const handleShow = (item) => {
        const id = item?._id || item?.id;
        const nextViews = getViews(item) + 1;

        if (id) {
            setBusinessNews(prev =>
                prev.map(n =>
                    n._id === id || n.id === id
                        ? { ...n, views: getViews(n) + 1 }
                        : n
                )
            );

            fetch(`${API_URL}/views/${id}`, { method: "PUT" }).catch(() => { });
        }

        setSelectedNews({ ...item, views: nextViews });
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
        setSelectedNews(null);
    };

    // ✅ SHARE
    const openNewTab = (url) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link copied!");
        } catch {
            window.prompt("Copy this link:", text);
        }
    };

    const shareTo = (platform, post) => {
        if (!post) return;
        const url = platform === "copy" ? getNewsShareUrl(post) : getNewsSocialShareUrl(post);
        const title = (post?.title || "News").trim();
        const text = `${title}\n${url}`;

        switch (platform) {
            case "whatsapp":
                openNewTab(`https://wa.me/?text=${encodeURIComponent(text)}`);
                break;
            case "facebook":
                openNewTab(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);
                break;
            case "x":
                openNewTab(`https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`);
                break;
            case "telegram":
                openNewTab(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`);
                break;
            case "linkedin":
                openNewTab(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`);
                break;
            case "email":
                openNewTab(`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(text)}`);
                break;
            case "copy":
                copyToClipboard(url);
                break;
            default:
                break;
        }
    };

    const ShareMenu = ({ post }) => (
        <Dropdown as={ButtonGroup}>
            <Button
                size="sm"
                className="btn-red"
                onClick={() => shareTo("whatsapp", post)}
            >
                <i className="bi bi-share-fill me-1" /> Share
            </Button>

            <Dropdown.Toggle split size="sm" className="btn-red" />

            <Dropdown.Menu align="end">
                <Dropdown.Item onClick={() => shareTo("whatsapp", post)}>WhatsApp</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("facebook", post)}>Facebook</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("x", post)}>X</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("telegram", post)}>Telegram</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("linkedin", post)}>LinkedIn</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={() => shareTo("email", post)}>Email</Dropdown.Item>
                <Dropdown.Item onClick={() => shareTo("copy", post)}>Copy Link</Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );

    return (
        <>
            <div className="business-hero text-white text-center py-5">
                <Container>
                    <h1 className="fw-bold display-5">Business News</h1>
                    <p className="lead">Latest updates on economy & market</p>
                </Container>
            </div>

            <Container className="py-5">
                {businessNews.length === 0 ? (
                    <h5 className="text-center text-muted">
                        No Business News Available
                    </h5>
                ) : (
                    <Row className="g-4">
                        {businessNews.map((news, i) => (
                            <Col md={4} sm={6} key={news._id || news.id || i}>
                                <Card className="card-hover h-100">

                                    <div className="thumb-wrap">
                                        <Card.Img
                                            src={getImage(news)}
                                            className="thumb"
                                            alt={news.title}
                                            onError={handleImgError}
                                        />
                                    </div>

                                    <Card.Body>
                                        <div className="d-flex justify-content-between mb-2">
                                            <Badge bg="danger">{news.category}</Badge>

                                            <span className="news-views">
                                                <i className="bi bi-eye me-1" />
                                                {formatViews(getViews(news))}
                                            </span>
                                        </div>

                                        <Card.Title className="title-sm">
                                            {news.title}
                                        </Card.Title>

                                        <Card.Text className="text-muted news-description">
                                            {news.shortDesc}
                                        </Card.Text>

                                        <div className="d-flex gap-2 flex-wrap">
                                            <Button
                                                as={Link}
                                                to={`/news-details/${news._id || news.id}`}
                                                size="sm"
                                                className="btn-red"
                                            >
                                                Read More
                                            </Button>

                                            <ShareMenu post={news} />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            <Modal show={show} onHide={handleClose} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedNews?.title}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {selectedNews && (
                        <>
                            <img
                                src={getImage(selectedNews)}
                                className="w-100 rounded mb-3"
                                alt={selectedNews.title}
                                onError={handleImgError}
                            />

                            <p className="news-article-text">{selectedNews.content}</p>

                            <div className="d-flex justify-content-between mt-3">
                                <span className="news-views">
                                    <i className="bi bi-eye me-1" />
                                    {formatViews(getViews(selectedNews))}
                                </span>

                                <ShareMenu post={selectedNews} />
                            </div>
                        </>
                    )}
                </Modal.Body>
            </Modal>

            <style>{`
                .business-hero {
                    background: linear-gradient(135deg, #d10000, #7a0000);
                }

                .btn-red {
                    background: #d10000;
                    border: none;
                    color: white;
                    font-weight: 700;
                }

                .btn-red:hover {
                    background: #a80000;
                }

                .card-hover {
                    border-radius: 14px;
                    box-shadow: 0 6px 18px rgba(0,0,0,0.06);
                    transition: transform 0.3s, box-shadow 0.3s;
                }

                .card-hover:hover {
                    transform: translateY(-8px);
                    box-shadow: 0 18px 40px rgba(0,0,0,0.15);
                }

                .thumb-wrap {
                    overflow: hidden;
                    border-radius: 14px 14px 0 0;
                }

                .thumb {
                    height: 200px;
                    width: 100%;
                    object-fit: cover;
                    transition: transform 0.3s;
                }

                .card-hover:hover .thumb {
                    transform: scale(1.05);
                }

                .news-views {
                    font-size: 13px;
                    color: #6c757d;
                    font-weight: 700;
                }
            `}</style>
        </>
    );
};

export default Business;